/**
 * useReviewMaintenanceTriage — supervisor review action on the AI
 * triage suggestion attached to a maintenance issue (PROJECT_STATE
 * task #66).
 *
 * Two modes:
 *   - mode='apply':    copy the AI suggestion (severity, category_id,
 *                      blocks_readiness) into the actual columns AND
 *                      stamp ai_triage_reviewed_at / _by_user_id.
 *   - mode='override': stamp ai_triage_reviewed_at / _by_user_id only;
 *                      leave the actual columns as-is so the supervisor
 *                      can edit manually via the existing flow.
 *
 * The DB-level BEFORE UPDATE trigger
 * (`maintenance_issue_lock_ai_columns`) prevents authenticated callers
 * from writing the `ai_suggested_*` / `ai_triage_status` columns. The
 * supervisor's review action only writes the *actual* fields plus the
 * two `ai_triage_reviewed_*` columns — both of those remain writable
 * by authenticated supervisors.
 *
 * RLS: relies on the existing maintenance_issues UPDATE policy for the
 * supervisor / contractor_admin / super_admin role. Cleaners and
 * maintenance-only roles cannot review (their RLS rejects the UPDATE).
 */

import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query'
import { useSupabase }                                          from './useSupabase'
import { useAuthStore }                                         from '@/store/authStore'
import { AppError }                                             from '@/lib/errors/AppError'
import type { MaintenanceIssueRow }                             from './useMaintenanceIssues'

export type ReviewMode = 'apply' | 'override'

export interface ReviewVars {
  issue: MaintenanceIssueRow
  mode:  ReviewMode
}

export interface ReviewResult {
  id:                            string
  ai_triage_reviewed_at:         string
  ai_triage_reviewed_by_user_id: string
}

export function useReviewMaintenanceTriage(): UseMutationResult<ReviewResult, AppError, ReviewVars> {
  const supabase    = useSupabase()
  const queryClient = useQueryClient()
  const userId      = useAuthStore((s) => s.user?.id ?? null)

  return useMutation<ReviewResult, AppError, ReviewVars>({
    mutationFn: async ({ issue, mode }): Promise<ReviewResult> => {
      if (!userId) {
        throw new AppError({
          code:        'UNAUTHENTICATED',
          message:     'No user id in auth store',
          userMessage: 'Please sign in again before reviewing this suggestion.',
          status:      401,
        })
      }
      if (issue.ai_triage_status !== 'suggested') {
        throw new AppError({
          code:        'NO_SUGGESTION',
          message:     `cannot apply review when ai_triage_status is "${issue.ai_triage_status ?? 'null'}"`,
          userMessage: 'There is no AI suggestion to review on this issue.',
          status:      422,
        })
      }
      if (mode === 'apply') {
        if (issue.ai_suggested_severity === null || issue.ai_suggested_is_blocking === null) {
          throw new AppError({
            code:        'INCOMPLETE_SUGGESTION',
            message:     'AI suggestion is missing severity or blocking flag',
            userMessage: 'AI suggestion is incomplete — choose Override and triage manually.',
            status:      422,
          })
        }
      }

      const nowIso = new Date().toISOString()
      const patch: Record<string, unknown> = {
        ai_triage_reviewed_at:         nowIso,
        ai_triage_reviewed_by_user_id: userId,
      }
      if (mode === 'apply') {
        patch.severity         = issue.ai_suggested_severity
        patch.blocks_readiness = issue.ai_suggested_is_blocking
        // category_id is allowed to be NULL — the AI may not have matched a
        // category, in which case we leave the actual category_id alone.
        if (issue.ai_suggested_category_id !== null) {
          patch.category_id = issue.ai_suggested_category_id
        }
      }

      const { data, error } = await supabase
        .from('maintenance_issues')
        .update(patch)
        .eq('id', issue.id)
        .select('id, ai_triage_reviewed_at, ai_triage_reviewed_by_user_id')
        .single()

      if (error) {
        const code = mapPgErrorCode(error.code)
        throw new AppError({
          code,
          message:     error.message,
          userMessage: userMessageFor(code, error.message),
          status:      403,
        })
      }
      return data as unknown as ReviewResult
    },

    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['maintenance-issues'] })
    },
  })
}

function mapPgErrorCode(pgCode: string | undefined): string {
  switch (pgCode) {
    case '42501':    return 'FORBIDDEN'
    case 'PGRST116': return 'NOT_FOUND'
    default:         return 'UPDATE_FAILED'
  }
}

function userMessageFor(code: string, fallback: string): string {
  switch (code) {
    case 'FORBIDDEN':  return 'You do not have permission to review this suggestion.'
    case 'NOT_FOUND':  return 'This issue could not be found or you cannot see it anymore.'
    default:           return fallback || 'Could not record the review.'
  }
}
